<template>
  <v-dialog
    :model-value="modelValue"
    @update:modelValue="$emit('update:modelValue', $event)"
    max-width="900"
  >
    <v-card rounded="xl" class="detail-card">
      <v-toolbar density="comfortable" class="detail-toolbar" flat>
        <div class="custom-title">
          <span
            class="editable-title"
            :contenteditable="isEditing"
            spellcheck="false"
            ref="titleEl"
            @keydown.enter.prevent="finishTitleEdit"
            @blur="finishTitleEdit"
          >
            {{ localTitle }}
          </span>
        </div>

        <v-spacer />

        <v-btn
          v-if="isEditing"
          icon
          variant="text"
          :disabled="!canUndo"
          title="Deshacer (Ctrl+Z)"
          @click="undo"
        >
          <v-icon>mdi-undo</v-icon>
        </v-btn>

        <v-btn
          v-if="isEditing"
          icon
          variant="text"
          :disabled="!canRedo"
          title="Rehacer (Ctrl+Y)"
          @click="redo"
        >
          <v-icon>mdi-redo</v-icon>
        </v-btn>

        <v-btn
          v-if="isEditing"
          icon
          variant="text"
          color="warning"
          title="Revertir cambios"
          @click="revertChanges"
        >
          <v-icon>mdi-restore</v-icon>
        </v-btn>

        <v-btn
          icon
          variant="text"
          @click="toggleEdit"
          :title="isEditing ? 'Guardar' : 'Editar'"
        >
          <v-icon>{{ isEditing ? "mdi-content-save" : "mdi-pencil" }}</v-icon>
        </v-btn>

        <v-btn icon variant="text" @click="$emit('update:modelValue', false)">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-divider />

      <v-card-text class="pa-5">
        <div class="text-caption text-medium-emphasis mb-2">
          {{ $t("his_user") }}
        </div>

        <v-card variant="outlined" rounded="lg" class="story-box">
          <v-card-text class="story-text">
            <div
              class="editable-body"
              :contenteditable="isEditing"
              spellcheck="false"
              @blur="finishBodyEdit"
              ref="bodyEl"
            >
              {{ localStoryText || "Sin contenido." }}
            </div>
          </v-card-text>
        </v-card>

        <div v-if="isEditing" class="text-caption text-medium-emphasis mt-2">
          {{ $t("tip2") }}
        </div>
      </v-card-text>

      <v-divider />

      <v-card-actions class="pa-4 justify-end">
        <v-btn variant="outlined" @click="$emit('update:modelValue', false)">
          {{ $t("cerrar2") }}
        </v-btn>
        <v-btn v-if="isEditing" variant="tonal" @click="saveAll">
          {{ $t("guardar") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: "UserStoryDetailDialog",
  props: {
    modelValue: { type: Boolean, default: false },
    userStory: { type: Object, default: null },
    projectName: { type: String, default: "" },
  },
  emits: ["update:modelValue", "save"],
  data() {
    return {
      isEditing: false,
      localTitle: "",
      localStoryText: "",
      originalSnapshot: null,
      history: [],
      historyIndex: -1,
    };
  },
  computed: {
    canUndo() {
      return this.isEditing && this.historyIndex > 0;
    },
    canRedo() {
      return this.isEditing && this.historyIndex < this.history.length - 1;
    },
  },
  watch: {
    userStory: {
      immediate: true,
      handler(v) {
        this.localTitle = (v?.title || "").toString();
        this.localStoryText = (v?.storyText || "").toString();
      },
    },
    modelValue(val) {
      if (val) {
        this.$nextTick(() => this.syncEditableDom());
        this.addHotkeys();
      } else {
        this.removeHotkeys();
        this.isEditing = false;
        this.originalSnapshot = null;
        this.history = [];
        this.historyIndex = -1;
      }
    },
    isEditing(val) {
      if (val) this.addHotkeys();
      else this.removeHotkeys();
    },
  },
  beforeUnmount() {
    this.removeHotkeys();
  },
  methods: {
    makeSnapshot() {
      return JSON.parse(
        JSON.stringify({
          title: this.localTitle,
          storyText: this.localStoryText,
        }),
      );
    },

    pushHistory() {
      if (!this.isEditing) return;

      const snap = this.makeSnapshot();
      const current = this.history[this.historyIndex];

      if (current && JSON.stringify(current) === JSON.stringify(snap)) return;

      if (this.historyIndex < this.history.length - 1) {
        this.history = this.history.slice(0, this.historyIndex + 1);
      }

      this.history.push(snap);
      this.historyIndex = this.history.length - 1;
    },

    undo() {
      if (!this.canUndo) return;
      this.historyIndex--;
      this.applySnapshot(this.history[this.historyIndex]);
    },

    redo() {
      if (!this.canRedo) return;
      this.historyIndex++;
      this.applySnapshot(this.history[this.historyIndex]);
    },

    applySnapshot(snap) {
      if (!snap) return;
      this.localTitle = snap.title || "";
      this.localStoryText = snap.storyText || "";
      this.$nextTick(() => this.syncEditableDom());
    },

    addHotkeys() {
      this.removeHotkeys();
      window.addEventListener("keydown", this.onGlobalKeydown, {
        passive: false,
      });
    },

    removeHotkeys() {
      window.removeEventListener("keydown", this.onGlobalKeydown);
    },

    onGlobalKeydown(e) {
      if (!this.modelValue || !this.isEditing) return;

      const key = (e.key || "").toLowerCase();
      const meta = e.ctrlKey || e.metaKey;

      if (meta && key === "s") {
        e.preventDefault();
        this.saveAll();
        return;
      }

      if (meta && key === "z" && !e.shiftKey) {
        e.preventDefault();
        this.undo();
        return;
      }

      if (meta && (key === "y" || (key === "z" && e.shiftKey))) {
        e.preventDefault();
        this.redo();
      }
    },

    toggleEdit() {
      this.isEditing = !this.isEditing;

      this.$nextTick(() => {
        if (this.isEditing) {
          this.originalSnapshot = this.makeSnapshot();
          this.history = [this.makeSnapshot()];
          this.historyIndex = 0;
          this.syncEditableDom();
          this.focusTitleEnd();
        } else {
          this.saveAll();
        }
      });
    },

    syncEditableDom() {
      if (this.$refs.titleEl)
        this.$refs.titleEl.innerText = this.localTitle || "Detalle";
      if (this.$refs.bodyEl)
        this.$refs.bodyEl.innerText = this.localStoryText || "Sin contenido.";
    },

    focusTitleEnd() {
      const el = this.$refs.titleEl;
      if (!el) return;
      el.focus();
      const range = document.createRange();
      range.selectNodeContents(el);
      range.collapse(false);
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);
    },

    finishTitleEdit() {
      if (!this.isEditing) return;
      const el = this.$refs.titleEl;
      const text = (el?.innerText || "").trim();
      this.localTitle = text;
      this.pushHistory();
    },

    finishBodyEdit() {
      if (!this.isEditing) return;
      const el = this.$refs.bodyEl;
      const text = (el?.innerText || "").replace(/\r\n/g, "\n");
      this.localStoryText = text.trimEnd();
      this.pushHistory();
    },

    revertChanges() {
      if (!this.originalSnapshot) return;
      this.applySnapshot(this.originalSnapshot);
      this.history = [this.makeSnapshot()];
      this.historyIndex = 0;
    },

    saveAll() {
      this.finishTitleEdit();
      this.finishBodyEdit();
      this.$emit("update:modelValue", false);

      this.$emit("save", {
        ...this.userStory,
        title: this.localTitle,
        storyText: this.localStoryText,
      });
    },
  },
};
</script>

<style scoped>
.detail-card {
  border-radius: 18px;
}
.detail-toolbar {
  background: transparent;
}
.story-box {
  border-radius: 14px;
}
.story-text {
  white-space: pre-wrap;
  line-height: 1.5;
}

.editable-title,
.editable-body {
  outline: none;
  border-radius: 8px;
  padding: 2px 6px;
}

.editable-title[contenteditable="true"],
.editable-body[contenteditable="true"] {
  border: 1px dashed rgba(255, 255, 255, 0.35);
  background: rgba(255, 255, 255, 0.05);
}

.editable-body {
  white-space: pre-wrap;
}
.v-toolbar-title {
  width: 100% !important;
}
.v-toolbar-title__placeholder {
  white-space: normal !important;
  overflow: visible !important;
  text-overflow: unset !important;
}
.custom-title {
  font-weight: 700;
  font-size: 22px;
  line-height: 1.4;
  white-space: pre-wrap;
  word-break: break-word;
}

.v-toolbar__content {
  overflow: visible !important;
}
</style>
